/**
 * Authentication Type Definitions
 */

/**
 * Organization information
 */
export interface Organization {
  id: number;
  code: string;
  name: string;
  businessTypeCode: string;
}

/**
 * Login request payload
 */
export interface LoginRequest {
  username: string;
  password: string;
}

/**
 * Login response from backend
 */
export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  username: string;  // V107: Primary identifier (로그인 ID)
  name: string;      // Display name (실명)
  email: string;     // Email (변경 가능)
  roles: string[];
  organization?: Organization;  // Organization information (optional for backward compatibility)
}

/**
 * Refresh token request payload
 */
export interface RefreshTokenRequest {
  refreshToken: string;
}

/**
 * Refresh token response from backend
 */
export interface RefreshTokenResponse {
  accessToken: string;
}

/**
 * User information
 */
export interface User {
  username: string;  // V107: Primary identifier (로그인 ID)
  name: string;      // Display name (실명)
  email: string;     // Email (변경 가능)
  roles: string[];
  organization?: Organization;  // Organization information (optional for backward compatibility)
}

/**
 * Authentication state
 */
export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * User roles type (V108: 4-tier → V1173: 5-tier role structure)
 * SUPER_ADMIN > OPERATOR > ORG_ADMIN > ORG_POWER > ORG_USER
 */
export const UserRole = {
  SUPER_ADMIN: 'SUPER_ADMIN' as const,  // Coreal 최상위 관리자
  OPERATOR: 'OPERATOR' as const,         // 서비스 운영자 (플랫폼 관리)
  ADMIN: 'ADMIN' as const,               // 서비스 운영 관리자 (레거시, OPERATOR와 동일 수준)
  ORG_ADMIN: 'ORG_ADMIN' as const,       // 조직(병원) 관리자 — 설정 변경 가능
  ORG_POWER: 'ORG_POWER' as const,       // 조직(병원) 파워유저 — 설정 조회전용
  ORG_USER: 'ORG_USER' as const,         // 조직(병원) 일반 사용자 — 설정 접근 불가
};

export type UserRole = (typeof UserRole)[keyof typeof UserRole];

/**
 * Check if user has specific role
 */
export const hasRole = (user: User | null, role: string): boolean => {
  if (!user || !user.roles) {
    return false;
  }
  return user.roles.includes(role);
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (user: User | null, roles: string[]): boolean => {
  if (!user || !user.roles) {
    return false;
  }
  return roles.some((role) => user.roles.includes(role));
};

/**
 * Check if user is admin (SUPER_ADMIN or ADMIN)
 */
export const isAdmin = (user: User | null): boolean => {
  return hasAnyRole(user, [UserRole.SUPER_ADMIN, UserRole.ADMIN]);
};

/**
 * Check if user is power user or admin
 * POWER includes: SUPER_ADMIN, ADMIN, ORG_ADMIN, ORG_POWER
 */
export const isPowerUser = (user: User | null): boolean => {
  return hasAnyRole(user, [
    UserRole.SUPER_ADMIN,
    UserRole.ADMIN,
    UserRole.ORG_ADMIN,
    UserRole.ORG_POWER,
  ]);
};

/**
 * Check if user is platform admin (SUPER_ADMIN, ADMIN, OPERATOR)
 * Platform admins have access to cross-organization management features
 */
export const isPlatformAdmin = (user: User | null): boolean => {
  return hasAnyRole(user, ['SUPER_ADMIN', 'ADMIN', 'OPERATOR']);
};

/**
 * V1173: 5-tier 역할별 세분화 헬퍼 함수
 */

/** SUPER_ADMIN 여부 */
export const isSuperAdmin = (user: User | null): boolean =>
  hasRole(user, UserRole.SUPER_ADMIN);

/** OPERATOR 여부 (ADMIN 포함 — 레거시 호환) */
export const isOperator = (user: User | null): boolean =>
  hasAnyRole(user, [UserRole.OPERATOR, UserRole.ADMIN]);

/** ORG_ADMIN 여부 — 조직 설정 변경 가능한 최소 역할 */
export const isOrgAdmin = (user: User | null): boolean =>
  hasRole(user, UserRole.ORG_ADMIN);

/** ORG_POWER 여부 — 설정 조회전용, 저장 불가 */
export const isOrgPower = (user: User | null): boolean =>
  hasRole(user, UserRole.ORG_POWER);

/** ORG_USER 여부 — 설정 메뉴 자체에 접근 불가 */
export const isOrgUser = (user: User | null): boolean =>
  hasRole(user, UserRole.ORG_USER);

/**
 * 설정 페이지 접근 가능 여부 (ORG_USER는 접근 불가)
 * ORG_ADMIN, ORG_POWER, 플랫폼 관리자만 허용
 */
export const canAccessSettings = (user: User | null): boolean =>
  !isOrgUser(user) && (isPlatformAdmin(user) || isOrgAdmin(user) || isOrgPower(user));

/**
 * Change password request payload
 *
 * ✅ 수정 (2025-10-31): confirmPassword 필드 추가
 * - Backend DTO와 일치하도록 수정
 */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string; // ✅ 추가: 비밀번호 확인
}

/**
 * Change password response from backend
 */
export interface ChangePasswordResponse {
  message: string;
}
